import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { StatusBadge, Icon } from '@carrent/ui'
import { useAuthStore } from '@carrent/core'
import { CAR_BODY_TYPES as BODY_TYPES } from '@carrent/domain'
import { CarHistoryTab } from './CarHistoryTab'
import { api } from '@carrent/api'
import CarBodyTab from './CarBodyTab'
import { isCarIcon, CarIconBanner, IconPickerModal } from '@carrent/ui'
import { TransferCarModal } from '../../components/TransferCarModal'
import { HistoryLink } from '../../components/HistoryLink'
import { RegionBadge } from '../../components/RegionPicker'
import { repairsApi } from '@carrent/api'

const DOC_TYPES = [
  { value: 'sts',          label: 'СТС',              icon: '📋',
    field: 'number',  fieldLabel: 'Номер СТС',         placeholder: '78 ОТ 757744' },
  { value: 'insurance',    label: 'Страховка (ОСАГО)', icon: '🛡',
    field: 'expires', fieldLabel: 'Срок действия' },
  { value: 'inspection',   label: 'Техосмотр',         icon: '🔍',
    field: 'expires', fieldLabel: 'Срок действия' },
  { value: 'service_book', label: 'Сервисная книжка',  icon: '📖',
    field: 'none' },
  { value: 'other',        label: 'Другое',             icon: '📄',
    field: 'none' },
]

const CAR_STATUSES = [
  { value: 'available',           label: 'Доступна',       color: 'var(--success)' },
  { value: 'maintenance',         label: 'На ТО',           color: 'var(--warning)' },
  { value: 'repair',              label: 'Ремонт',          color: '#f97316'        },
  { value: 'inspection_required', label: 'Нужен осмотр',   color: 'var(--warning)' },
  { value: 'blocked',             label: 'Заблокирована',   color: 'var(--danger)'  },
]


function InfoRow({ label, value, children }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--gap-sm)', padding: '10px var(--gap)',
      borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
      <span className="text-muted" style={{ minWidth: 130, fontSize: '0.82rem', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.9rem' }}>{children || value || '—'}</span>
    </div>
  )
}


// ── Таб финансов — редирект на страницу ──────────────────────────────────────
// Автоцвет плейсхолдера по строке (чтобы разные машины различались)
function brandColor(str) {
  let h = 0
  for (let i = 0; i < (str || '').length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  const hue = Math.abs(h) % 360
  return `hsl(${hue}, 40%, 28%)`
}

// Фото машины с фолбэком при ошибке загрузки
function CarPhotoBlock({ photoMain, brand, model, plate }) {
  const [broken, setBroken] = useState(false)
  const showPlaceholder = !photoMain || broken

  if (!showPlaceholder) {
    return (
      <img
        src={photoMain}
        alt={plate}
        onError={() => setBroken(true)}
        style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
      />
    )
  }

  // Красивый placeholder — цветной градиент + силуэт авто
  const bg = brandColor(`${brand}${model}`)
  return (
    <div style={{
      height: 200, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 8,
      background: `linear-gradient(160deg, ${bg} 0%, #0d1117 100%)`,
    }}>
      <svg viewBox="0 0 120 50" style={{ width: 140, opacity: 0.55 }}>
        <path d="M8 38 C8 38 12 28 28 26 L40 20 L70 18 L88 26 C100 28 112 34 112 38 L112 42 L8 42 Z"
          fill="rgba(255,255,255,0.6)" />
        <circle cx="28" cy="42" r="6" fill="rgba(255,255,255,0.4)" />
        <circle cx="92" cy="42" r="6" fill="rgba(255,255,255,0.4)" />
        <path d="M42 20 L50 12 L72 12 L80 20" fill="rgba(255,255,255,0.3)" />
      </svg>
      <div style={{ textAlign: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 700, fontSize: '0.95rem' }}>
          {brand} {model}
        </p>
        {broken && (
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', marginTop: 2 }}>
            Фото недоступно
          </p>
        )}
      </div>
    </div>
  )
}

// ── Таб ремонтов ─────────────────────────────────────────────────────────────
const REPAIR_TYPE_OPTIONS = [
  { v: 'to',         l: '🔩 Плановое ТО'      },
  { v: 'repair',     l: '🔧 Ремонт'            },
  { v: 'body',       l: '🎨 Кузовные работы'   },
  { v: 'tire',       l: '🛞 Шиномонтаж'        },
  { v: 'diagnostic', l: '🔍 Диагностика'       },
  { v: 'other',      l: '📝 Прочее'            },
]

function RepairsTab({ carId, car, onCarStatusChanged }) {
  const [data,       setData]       = useState(null)
  const [loading,    setLoading]    = useState(true)
  const [showForm,   setShowForm]   = useState(false)
  const [editRepair, setEditRepair] = useState(null)
  const [statusFilter, setStatusFilter] = useState('')

  const load = () => {
    setLoading(true)
    repairsApi.list(carId, statusFilter)
      .then(r => setData(r.data?.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [carId, statusFilter])

  const handleDelete = async (id) => {
    if (!confirm('Удалить запись о ремонте?')) return
    try {
      await repairsApi.delete(id)
      load()
    } catch (e) { alert(e.response?.data?.message || 'Ошибка') }
  }

  const handleClose = async (repair) => {
    try {
      await repairsApi.close(repair.id, 'available')
      load(); onCarStatusChanged?.()
    } catch (e) { alert(e.response?.data?.message || 'Ошибка') }
  }

  const repairs = data?.items || []
  const stats   = data?.stats || {}

  return (
    <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
      {/* Статистика */}
      <div className="card" style={{ display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)', padding: '10px 0' }}>
        {[
          { label: 'Всего',      value: stats.total ?? '—'                },
          { label: 'В работе',   value: stats.open  ?? '—'                },
          { label: 'Затраты',    value: stats.total_cost
              ? `${stats.total_cost.toLocaleString()} ₽` : '0 ₽'         },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: 'center', padding: '4px',
            borderLeft: i ? '1px solid var(--border)' : 'none' }}>
            <p style={{ fontSize: '1.4rem', fontWeight: 800 }}>{s.value}</p>
            <p className="text-muted" style={{ fontSize: '0.7rem' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Кнопка + фильтр */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button className="btn btn--primary" style={{ flexShrink: 0 }}
          onClick={() => { setEditRepair(null); setShowForm(true) }}>
          + Добавить
        </button>
        <div style={{ display: 'flex', gap: 4, overflowX: 'auto' }}>
          {[['', 'Все'], ['open', 'В работе'], ['done', 'Завершённые']].map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              style={{
                padding: '5px 12px', borderRadius: 99, whiteSpace: 'nowrap',
                border: `1px solid ${statusFilter === v ? 'var(--accent)' : 'var(--border)'}`,
                background: statusFilter === v ? 'var(--accent)' : 'var(--bg-card)',
                color: statusFilter === v ? '#fff' : 'var(--text-muted)',
                fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
              }}>{l}</button>
          ))}
        </div>
      </div>

      {loading && <div className="spinner" style={{ margin: '0 auto' }} />}

      {!loading && repairs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2.5rem 0', color: 'var(--text-muted)' }}>
          <p style={{ fontSize: '2.5rem', marginBottom: 8 }}>🔧</p>
          <p style={{ fontSize: '0.88rem' }}>Записей о ремонте нет</p>
        </div>
      )}

      {repairs.map(r => (
        <div key={r.id} className="card" style={{ padding: 'var(--gap-sm)' }}>
          {/* Шапка */}
          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px',
                  borderRadius: 99,
                  background: r.status === 'open' ? 'rgba(251,191,36,0.15)' : 'rgba(34,197,94,0.12)',
                  color: r.status === 'open' ? '#f59e0b' : '#22c55e',
                  border: `1px solid ${r.status === 'open' ? '#f59e0b44' : '#22c55e44'}`,
                }}>{r.status_label}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)',
                  padding: '2px 8px', background: 'var(--bg)', borderRadius: 99 }}>
                  {r.repair_type_label}
                </span>
              </div>
              <p style={{ fontWeight: 700, fontSize: '0.92rem', marginTop: 4 }}>{r.title}</p>
            </div>
            {/* Кнопки */}
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {r.status === 'open' && (
                <button onClick={() => handleClose(r)}
                  style={{ background: 'var(--success)', color: '#fff', border: 'none',
                    borderRadius: 'var(--r-sm)', padding: '4px 10px',
                    fontSize: '0.72rem', cursor: 'pointer' }}>
                  ✓ Завершить
                </button>
              )}
              <button onClick={() => { setEditRepair(r); setShowForm(true) }}
                style={{ background: 'none', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)', padding: '4px 8px',
                  fontSize: '0.72rem', cursor: 'pointer' }}>✏️</button>
              <button onClick={() => handleDelete(r.id)}
                style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 'var(--r-sm)', padding: '4px 8px',
                  fontSize: '0.72rem', cursor: 'pointer', color: 'var(--danger)' }}>🗑</button>
            </div>
          </div>

          {/* Детали */}
          {r.description && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)',
              marginBottom: 6, lineHeight: 1.4 }}>{r.description}</p>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: '0.78rem' }}>
            {r.workshop && (
              <span className="text-muted">🏭 {r.workshop}</span>
            )}
            {r.cost != null && (
              <span style={{ fontWeight: 700, color: 'var(--accent)' }}>
                💰 {r.cost.toLocaleString()} ₽
              </span>
            )}
            {r.started_at && (
              <span className="text-muted">
                📅 {r.started_at}{r.finished_at ? ` → ${r.finished_at}` : ''}
              </span>
            )}
            {r.mileage_at != null && (
              <span className="text-muted">🛣 {r.mileage_at.toLocaleString()} км</span>
            )}
          </div>
        </div>
      ))}

      {showForm && (
        <RepairForm
          car={car}
          repair={editRepair}
          onClose={() => { setShowForm(false); setEditRepair(null) }}
          onSaved={() => { setShowForm(false); setEditRepair(null); load(); onCarStatusChanged?.() }}
        />
      )}
    </div>
  )
}

function RepairForm({ car, repair, onClose, onSaved }) {
  const [form, setForm] = useState({
    repair_type:    repair?.repair_type    || 'to',
    title:          repair?.title          || '',
    description:    repair?.description    || '',
    workshop:       repair?.workshop       || '',
    cost:           repair?.cost           != null ? String(repair.cost) : '',
    started_at:     repair?.started_at     || '',
    finished_at:    repair?.finished_at    || '',
    mileage_at:     repair?.mileage_at     != null ? String(repair.mileage_at) : '',
    status:         repair?.status         || 'open',
    set_car_status: '',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr]       = useState('')

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const submit = async () => {
    if (!form.title.trim()) { setErr('Укажите название'); return }
    setSaving(true); setErr('')
    const payload = {
      ...form,
      cost:       form.cost       ? parseFloat(form.cost)   : null,
      mileage_at: form.mileage_at ? parseInt(form.mileage_at) : null,
      set_car_status: form.set_car_status || undefined,
    }
    try {
      if (repair) {
        await repairsApi.update(repair.id, payload)
      } else {
        await repairsApi.create(car.id, payload)
      }
      onSaved()
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка сохранения')
    } finally { setSaving(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)',
      display: 'flex', alignItems: 'flex-end', zIndex: 300 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: '100%', maxWidth: 480, margin: '0 auto',
        background: 'var(--bg-card)',
        borderRadius: 'var(--r-xl) var(--r-xl) 0 0',
        padding: 'var(--gap)',
        paddingBottom: 'calc(var(--gap) + env(safe-area-inset-bottom, 0px))',
        maxHeight: '92vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 'var(--gap)' }}>
          <h3 style={{ margin: 0 }}>{repair ? 'Редактировать' : 'Новая запись о ремонте'}</h3>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
        </div>

        <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
          {/* Тип */}
          <div className="field" style={{ margin: 0 }}>
            <label className="field__label">Тип</label>
            <select className="input" value={form.repair_type} onChange={set('repair_type')}>
              {REPAIR_TYPE_OPTIONS.map(o => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </div>

          {/* Название */}
          <div className="field" style={{ margin: 0 }}>
            <label className="field__label">Название *</label>
            <input className="input" value={form.title} onChange={set('title')}
              placeholder="Замена масла и фильтров" />
          </div>

          {/* Описание */}
          <div className="field" style={{ margin: 0 }}>
            <label className="field__label">Описание</label>
            <textarea className="input" rows={2} value={form.description}
              onChange={set('description')}
              placeholder="Подробности работ..."
              style={{ resize: 'vertical' }} />
          </div>

          {/* Мастерская */}
          <div className="field" style={{ margin: 0 }}>
            <label className="field__label">Мастерская / Механик</label>
            <input className="input" value={form.workshop} onChange={set('workshop')}
              placeholder="Сервис «Авто+»" />
          </div>

          {/* Стоимость + пробег */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-sm)' }}>
            <div className="field" style={{ margin: 0 }}>
              <label className="field__label">Стоимость (₽)</label>
              <input className="input" type="number" min="0" value={form.cost}
                onChange={set('cost')} placeholder="0" />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label className="field__label">Пробег (км)</label>
              <input className="input" type="number" min="0" value={form.mileage_at}
                onChange={set('mileage_at')} placeholder={car?.mileage || ''} />
            </div>
          </div>

          {/* Даты */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-sm)' }}>
            <div className="field" style={{ margin: 0 }}>
              <label className="field__label">Начало</label>
              <input className="input" type="date" value={form.started_at}
                onChange={set('started_at')} />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label className="field__label">Конец</label>
              <input className="input" type="date" value={form.finished_at}
                onChange={set('finished_at')} />
            </div>
          </div>

          {/* Статус ремонта */}
          <div className="field" style={{ margin: 0 }}>
            <label className="field__label">Статус записи</label>
            <select className="input" value={form.status} onChange={set('status')}>
              <option value="open">В работе</option>
              <option value="done">Завершён</option>
            </select>
          </div>

          {/* Сменить статус машины */}
          <div style={{
            padding: 'var(--gap-sm)',
            background: 'var(--bg)', borderRadius: 'var(--r-sm)',
            border: '1px solid var(--border)',
          }}>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>
              Также сменить статус машины:
              <span style={{ marginLeft: 6, fontWeight: 600,
                color: 'var(--text)' }}>{car?.status}</span>
            </p>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[
                { v: '',            l: 'Не менять',  },
                { v: 'maintenance', l: 'На ТО'       },
                { v: 'repair',      l: 'Ремонт'      },
                { v: 'available',   l: 'Доступна'    },
              ].map(o => (
                <button key={o.v} type="button"
                  onClick={() => setForm(f => ({ ...f, set_car_status: o.v }))}
                  style={{
                    padding: '5px 10px', borderRadius: 99, fontSize: '0.75rem',
                    border: `1px solid ${form.set_car_status === o.v ? 'var(--accent)' : 'var(--border)'}`,
                    background: form.set_car_status === o.v ? 'var(--accent)' : 'var(--bg-card)',
                    color: form.set_car_status === o.v ? '#fff' : 'var(--text-muted)',
                    cursor: 'pointer',
                  }}>{o.l}</button>
              ))}
            </div>
          </div>

          {err && <div className="alert alert--error">{err}</div>}

          <button className="btn btn--primary btn--full" style={{ height: 50 }}
            disabled={saving || !form.title.trim()} onClick={submit}>
            {saving ? <span className="spinner" /> : (repair ? 'Сохранить' : 'Добавить запись')}
          </button>
        </div>
      </div>
    </div>
  )
}

function FinanceTab({ carId }) {
  const navigate = useNavigate()
  useEffect(() => {
    navigate(`/admin/cars/${carId}/finance`)
  }, [])
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="spinner" /></div>
}

// ── Таб поездок ───────────────────────────────────────────────────────────────
function TripsTab({ carId }) {
  const navigate = useNavigate()
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage]       = useState(1)
  const [statusFilter, setStatusFilter] = useState('')

  const [err, setErr] = useState('')

  const load = (p = 1, s = statusFilter) => {
    setLoading(true); setErr('')
    api.get(`/trips/admin/by-car/${carId}`, {
      params: { page: p, per_page: 20, ...(s && { status: s }) }
    })
      .then(r => setData(r.data?.data))
      .catch(e => setErr(e.response?.data?.message || 'Ошибка загрузки поездок'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load(1) }, [carId])

  const changeStatus = (s) => {
    setStatusFilter(s); setPage(1); load(1, s)
  }

  const STATUS_LABELS = {
    active:              { l: 'Активна',     color: '#3b82f6' },
    finished_by_user:    { l: 'Ждёт подтверждения', color: '#f59e0b' },
    confirmed_by_admin:  { l: 'Завершена',   color: '#22c55e' },
    cancelled:           { l: 'Отменена',    color: '#6b7280' },
  }

  const fmtDate = (iso) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })
  }

  const fmtKm = (km) => km != null ? `${km.toLocaleString()} км` : '—'

  if (loading && !data) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
      <div className="spinner" />
    </div>
  )

  if (err) return (
    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--danger)' }}>
      <p style={{ fontSize: '1.5rem', marginBottom: 8 }}>⚠️</p>
      <p style={{ fontSize: '0.85rem' }}>{err}</p>
      <button className="btn btn--outline" style={{ marginTop: 12 }} onClick={() => load()}>
        Повторить
      </button>
    </div>
  )

  const trips = data?.items || []
  const meta  = data?.meta  || {}
  const stats = data?.stats || {}

  return (
    <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
      {/* Статистика */}
      <div className="card" style={{ display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)', padding: '10px 0' }}>
        {[
          { label: 'Всего поездок', value: stats.total ?? '—' },
          { label: 'Активных',      value: stats.active ?? '—' },
          { label: 'Пройдено',      value: stats.total_km ? `${stats.total_km.toLocaleString()} км` : '0 км' },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: 'center', padding: '4px',
            borderLeft: i ? '1px solid var(--border)' : 'none' }}>
            <p style={{ fontSize: '1.4rem', fontWeight: 800 }}>{s.value}</p>
            <p className="text-muted" style={{ fontSize: '0.7rem' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Фильтр статуса */}
      <div style={{ display: 'flex', gap: 4, overflowX: 'auto', padding: '2px 0' }}>
        {[['', 'Все'], ['active', 'Активные'],
          ['finished_by_user', 'Ожидают'], ['confirmed_by_admin', 'Завершённые']
        ].map(([v, l]) => (
          <button key={v} onClick={() => changeStatus(v)}
            style={{
              padding: '5px 12px', borderRadius: 99, whiteSpace: 'nowrap',
              border: `1px solid ${statusFilter === v ? 'var(--accent)' : 'var(--border)'}`,
              background: statusFilter === v ? 'var(--accent)' : 'var(--bg-card)',
              color: statusFilter === v ? '#fff' : 'var(--text-muted)',
              fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            }}>
            {l}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
        </div>
      )}

      {!loading && trips.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
          <p style={{ fontSize: '2.5rem', marginBottom: 8 }}>🛣</p>
          <p style={{ fontSize: '0.88rem' }}>Поездок не найдено</p>
        </div>
      )}

      {trips.map(trip => {
        const st = STATUS_LABELS[trip.status] || { l: trip.status, color: '#888' }
        const km = trip.start_mileage != null && trip.end_mileage != null
          ? trip.end_mileage - trip.start_mileage : null

        return (
          <div key={trip.id} className="card"
            onClick={() => navigate(`/admin/trips/${trip.id}`)}
            style={{ padding: 'var(--gap-sm)', cursor: 'pointer' }}>

            {/* Верхняя строка: статус + дата */}
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 6 }}>
              <span style={{
                fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px',
                borderRadius: 99, background: `${st.color}22`, color: st.color,
                border: `1px solid ${st.color}44`,
              }}>{st.l}</span>
              <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                #{trip.id}
              </span>
            </div>

            {/* Водитель */}
            {trip.user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: '1rem' }}>🚙</span>
                <p style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                  {trip.user.full_name}
                </p>
                <p className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {trip.user.phone}
                </p>
              </div>
            )}

            {/* Время и км */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: 4, fontSize: '0.78rem' }}>
              <div>
                <span className="text-muted">Начало: </span>
                <span>{fmtDate(trip.started_at)}</span>
              </div>
              <div>
                <span className="text-muted">Конец: </span>
                <span>{fmtDate(trip.ended_at)}</span>
              </div>
              <div>
                <span className="text-muted">Пробег: </span>
                <span style={{ fontWeight: 700 }}>
                  {km != null ? `${km.toLocaleString()} км` : '—'}
                </span>
              </div>
              {trip.start_mileage != null && (
                <div>
                  <span className="text-muted">Спидометр: </span>
                  <span>{fmtKm(trip.start_mileage)} → {fmtKm(trip.end_mileage)}</span>
                </div>
              )}
            </div>
          </div>
        )
      })}

      {/* Пагинация */}
      {meta.pages > 1 && (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
          {Array.from({ length: meta.pages }, (_, i) => i + 1).map(p => (
            <button key={p}
              onClick={() => { setPage(p); load(p) }}
              style={{
                width: 34, height: 34, borderRadius: 8,
                border: `1px solid ${page === p ? 'var(--accent)' : 'var(--border)'}`,
                background: page === p ? 'var(--accent)' : 'var(--bg-card)',
                color: page === p ? '#fff' : 'var(--text)',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: page === p ? 700 : 400,
              }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function AdminCarDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [car, setCar]             = useState(null)
  const [loading, setLoading]     = useState(true)
  const [msg, setMsg]             = useState(null)
  const [searchParams] = useSearchParams()
  // Вкладка берётся из адреса: гаджет «Учёт инцидентов» ведёт к повреждению
  // на схеме кузова, и открывать вместо неё общую карточку — терять шаг.
  const [tab, setTab]             = useState(
    () => searchParams.get('tab') || 'info',
  ) // info | trips | repairs | body | docs | finance | history | edit
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)
  const photoRef                  = useRef()

  // Менеджер правит только машины своих регионов — то же правило, что
  // на сервере (can_access_car). Дублирование осознанное: решает сервер,
  // интерфейс лишь не предлагает недоступного.
  const me = useAuthStore(st => st.user)
  const myRegions = (me?.regions || []).map(r => (typeof r === 'object' ? r.id : r))
  const canEdit =
    me?.role === 'admin'
      ? myRegions.length === 0 || myRegions.includes(car?.region_id)
      : me?.role === 'manager' && myRegions.includes(car?.region_id)

  const load = () => {
    setLoading(true)
    api.get(`/admin/cars/${id}`)
      .then(r => setCar(r.data?.data))
      .catch(() => setMsg({ type: 'error', text: 'Не удалось загрузить машину' }))
      .finally(() => setLoading(false))
  }
  useEffect(load, [id])

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000) }

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/cars/${id}`, { status })
      flash('success', 'Статус обновлён')
      load()
    } catch (e) {
      flash('error', e.response?.data?.message || 'Ошибка')
    }
  }

  const uploadPhoto = async (file) => {
    const fd = new FormData(); fd.append('file', file)
    try {
      await api.post(`/admin/cars/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      flash('success', 'Фото обновлено')
      load()
    } catch (e) {
      flash('error', e.response?.data?.message || 'Ошибка загрузки фото')
    }
  }

  const deletePhoto = async () => {
    if (!confirm('Удалить фото машины?')) return
    try {
      await api.delete(`/admin/cars/${id}/photo`)
      flash('success', 'Фото удалено')
      load()
    } catch (e) {
      flash('error', e.response?.data?.message || 'Ошибка')
    }
  }

  const selectIcon = async (iconValue) => {
    try {
      await api.patch(`/admin/cars/${id}`, { photo_main: iconValue })
      flash('success', 'Иконка установлена')
      load()
    } catch (e) {
      flash('error', e.response?.data?.message || 'Ошибка')
    }
  }

  if (loading) return (
    <div><PageHeader title="Загрузка..." backTo="/admin/cars" />
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    </div>
  )
  if (!car) return <div><PageHeader title="Не найдено" backTo="/admin/cars" /></div>

  return (
    <div style={{ paddingBottom: 'var(--gap-xl)' }}>
      <PageHeader
        title={`${car.brand} ${car.model}`}
        backTo="/admin/cars"
        right={
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <HistoryLink entityType="car" entityId={car.id} label="" />
            {car.region && <RegionBadge region={car.region} size="lg" />}
            <StatusBadge status={car.status} />
          </div>
        }
      />

      {/* Фото / Иконка + управление */}
      <div style={{ position: 'relative' }}>
        {isCarIcon(car.photo_main)
          ? <CarIconBanner src={car.photo_main} brand={car.brand} model={car.model} />
          : <CarPhotoBlock
              photoMain={car.photo_main}
              brand={car.brand}
              model={car.model}
              plate={car.plate}
            />
        }
        {/* Кнопки управления фото */}
        <div style={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 6 }}>
          {car.photo_main && (
            <button onClick={deletePhoto}
              style={{ background: 'rgba(239,68,68,0.8)', color: '#fff', border: 'none',
                borderRadius: 'var(--r-sm)', padding: '6px 10px',
                fontSize: '0.78rem', cursor: 'pointer' }}>
              🗑 Удалить
            </button>
          )}
          <button onClick={() => setShowIconPicker(true)}
            style={{ background: 'rgba(139,92,246,0.85)', color: '#fff', border: 'none',
              borderRadius: 'var(--r-sm)', padding: '6px 12px',
              fontSize: '0.82rem', cursor: 'pointer' }}>
            🎨 Иконка
          </button>
          <button onClick={() => photoRef.current?.click()}
            style={{ background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none',
              borderRadius: 'var(--r-sm)', padding: '6px 12px',
              fontSize: '0.82rem', cursor: 'pointer' }}>
            📷 Фото
          </button>
        </div>
        <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => e.target.files[0] && uploadPhoto(e.target.files[0])} />
      </div>

      {showIconPicker && (
        <IconPickerModal
          current={car.photo_main}
          onSelect={selectIcon}
          onClose={() => setShowIconPicker(false)}
        />
      )}

      {showTransfer && (
        <TransferCarModal
          car={car}
          onClose={() => setShowTransfer(false)}
          onTransferred={() => { setShowTransfer(false); flash('success', 'Машина переведена'); load() }}
        />
      )}

      <div className="p-page flex-col" style={{ gap: 'var(--gap)' }}>
        {msg && (
          <div className={`alert alert--${msg.type === 'success' ? 'success' : 'error'}`}>
            {msg.text}
          </div>
        )}

        {/* Госномер крупно */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{
            background: '#fff', color: '#000', padding: '6px 16px',
            borderRadius: 8, fontWeight: 800, fontSize: '1.1rem',
            letterSpacing: '0.1em', border: '2px solid #000',
            fontFamily: 'monospace',
          }}>
            {car.plate}
          </div>
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            {car.year} · {(car.mileage || 0).toLocaleString()} км
          </div>
        </div>

        {/* Табы */}
        <div style={{ display: 'flex', gap: 0, background: 'var(--bg-card)',
          borderRadius: 'var(--r)', padding: 4, border: '1px solid var(--border)' }}>
          {[
            { k: 'info',    l: 'Данные',        i: 'car' },
            { k: 'trips',   l: 'Поездки',       i: 'trips' },
            { k: 'repairs', l: 'Ремонт',        i: 'wrench' },
            { k: 'body',    l: 'Кузов',         i: 'columns' },
            { k: 'docs',    l: 'Документы',     i: 'file' },
            { k: 'finance', l: 'Финансы',       i: 'dashboard' },
            { k: 'history', l: 'Изменения',     i: 'clock' },
            // Вкладка редактирования показывается только тому, кто может
            // сохранить: менеджер правит лишь машины своих регионов,
            // и кнопка, ведущая в 403, хуже отсутствующей.
            ...(canEdit ? [{ k: 'edit', l: 'Редактировать', i: 'edit' }] : []),
          ].map(({ k, l, i }) => (
            <button key={k} onClick={() => setTab(k)} style={{
              flex: 1, padding: '8px 4px', border: 'none', borderRadius: 'var(--r-sm)',
              background: tab === k ? 'var(--accent)' : 'transparent',
              color: tab === k ? '#fff' : 'var(--text-muted)',
              fontSize: '0.78rem', fontWeight: tab === k ? 700 : 400, cursor: 'pointer',
              transition: 'all 0.15s',
            }}><Icon name={i} size={14} /> {l}</button>
          ))}
        </div>

        {/* ── Таб: Информация ──────────────────────── */}
        {tab === 'info' && (
          <>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <InfoRow label="Марка / Модель" value={`${car.brand} ${car.model}`} />
              <InfoRow label="VIN"   value={car.vin} />
              <InfoRow label="Цвет"  value={car.color} />
              <InfoRow label="Пробег" value={car.mileage ? `${car.mileage.toLocaleString()} км` : null} />
              <InfoRow label="Тип кузова">
                {car.body_type && car.body_type !== 'other'
                  ? <span>{car.body_type_label}{car.has_tail_lift ? ', гидроборт' : ''}</span>
                  : <span className="body-type__missing">
                      Не указан — схема повреждений недоступна
                    </span>}
              </InfoRow>
              {car.comment && <InfoRow label="Комментарий" value={car.comment} />}
            </div>

            {/* Регион */}
            <p className="section-title">Регион</p>
            <div className="card" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: 'var(--gap-sm) var(--gap)', gap: 'var(--gap-sm)',
            }}>
              <div style={{ flex: 1 }}>
                {car.region ? (
                  <>
                    <p style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                      {car.region.name}
                    </p>
                    <p className="text-muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>
                      Код: <span style={{ fontFamily: 'monospace' }}>{car.region.code}</span>
                    </p>
                  </>
                ) : (
                  <p className="text-muted" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                    Не привязана к региону
                  </p>
                )}
              </div>
              <button onClick={() => setShowTransfer(true)}
                style={{
                  background: 'var(--accent)', color: '#fff', border: 'none',
                  borderRadius: 'var(--r-sm)', padding: '8px 14px',
                  fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600,
                }}>
                🚚 Перевести
              </button>
            </div>

            {/* Смена статуса */}
            <p className="section-title">Статус</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-sm)' }}>
              {CAR_STATUSES.map(s => (
                <button key={s.value} onClick={() => setStatus(s.value)}
                  disabled={car.status === s.value || car.status === 'in_use'}
                  style={{
                    padding: '10px', border: `1px solid ${car.status === s.value ? s.color : 'var(--border)'}`,
                    borderRadius: 'var(--r)', background: car.status === s.value
                      ? `${s.color}20` : 'transparent',
                    color: car.status === s.value ? s.color : 'var(--text)',
                    cursor: car.status === s.value ? 'default' : 'pointer',
                    fontSize: '0.85rem', fontWeight: car.status === s.value ? 700 : 400,
                    transition: 'all 0.15s', opacity: car.status === 'in_use' ? 0.5 : 1,
                  }}>
                  {car.status === s.value ? '✓ ' : ''}{s.label}
                </button>
              ))}
            </div>
            {car.status === 'in_use' && (
              <p className="text-muted" style={{ fontSize: '0.82rem', textAlign: 'center' }}>
                Статус меняется автоматически — машина сейчас в поездке
              </p>
            )}

            {/* Напоминания */}
            {Object.values(car.reminders || {}).some(Boolean) && (
              <>
                <p className="section-title">Напоминания</p>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  {[
                    { label: 'Страховка до',   value: car.reminders.insurance_expires_at  },
                    { label: 'Техосмотр до',   value: car.reminders.inspection_expires_at },
                    { label: 'Замена масла',   value: car.reminders.oil_change_due_date    },
                    { label: 'Масло (км)',     value: car.reminders.oil_change_due_km ? `${car.reminders.oil_change_due_km.toLocaleString()} км` : null },
                    { label: 'Плановое ТО',   value: car.reminders.service_due_date        },
                  ].filter(r => r.value).map(({ label, value }) => {
                    const isDate = value && value.includes('-')
                    const isExpired = isDate && new Date(value) < new Date()
                    const isSoon = isDate && !isExpired && (new Date(value) - new Date()) < 14*24*60*60*1000
                    return (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between',
                        padding: '10px var(--gap)', borderBottom: '1px solid var(--border)' }}>
                        <span className="text-muted" style={{ fontSize: '0.82rem' }}>{label}</span>
                        <span style={{ fontSize: '0.88rem',
                          color: isExpired ? 'var(--danger)' : isSoon ? 'var(--warning)' : 'var(--text)',
                          fontWeight: (isExpired || isSoon) ? 600 : 400,
                        }}>
                          {isDate ? new Date(value).toLocaleDateString('ru') : value}
                          {isExpired && ' ⚠️ Истёк'}
                          {isSoon && ' ⏰'}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* ── Таб: Ремонт ──────────────────────────── */}
        {tab === 'repairs' && (
          <RepairsTab carId={id} car={car} onCarStatusChanged={load} />
        )}

        {/* ── Таб: Поездки ─────────────────────────── */}
        {tab === 'trips' && (
          <TripsTab carId={id} />
        )}

        {/* ── Таб: Документы ──────────────────────── */}
        {tab === 'docs' && (
          <DocTab carId={id} docs={car.documents || []} docsByType={car.docs_by_type || {}}
            onReload={load} onFlash={flash} />
        )}

        {/* ── Таб: Финансы ────────────────────────── */}
        {tab === 'finance' && (
          <FinanceTab carId={id} />
        )}

        {/* ── Таб: Кузов ──────────────────────────── */}
        {tab === 'body' && <CarBodyTab car={car} />}

        {tab === 'history' && (
          <CarHistoryTab
            car={car}
            dictionaries={{
              statuses: { available: 'Доступна', in_use: 'В поездке',
                maintenance: 'На ТО', repair: 'В ремонте',
                inspection_required: 'Нужен осмотр', blocked: 'Заблокирована' },
              regions: car.region ? { [car.region.id]: car.region.name } : {},
            }}
          />
        )}

        {/* ── Таб: Редактировать ──────────────────── */}
        {tab === 'edit' && (
          <EditTab car={car} onSaved={() => { flash('success', 'Изменения сохранены'); load(); setTab('info') }}
            onFlash={flash} />
        )}
      </div>
    </div>
  )
}

// ── Таб документов ────────────────────────────────────────────────────────────
function DocTab({ carId, docs, docsByType, onReload, onFlash }) {
  const [uploading, setUploading] = useState(false)
  const [form, setForm] = useState({ doc_type: 'sts', expires_at: '', doc_number: '', comment: '' })
  const fileRef = useRef()
  const [file, setFile] = useState(null)

  const handleUpload = async (e) => {
    e.preventDefault()
    if (!file) { onFlash('error', 'Выберите файл'); return }
    setUploading(true)
    const fd = new FormData()
    fd.append('file', file)
    fd.append('doc_type', form.doc_type)
    if (form.expires_at)  fd.append('expires_at', form.expires_at)
    // Номер документа (для СТС) сохраняем в поле comment
    const commentVal = form.doc_number
      ? `№ ${form.doc_number}`
      : (form.comment || '')
    if (commentVal) fd.append('comment', commentVal)
    try {
      await api.post(`/admin/cars/${carId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onFlash('success', 'Документ загружен')
      setFile(null); setForm({ doc_type: 'sts', expires_at: '', doc_number: '', comment: '' })
      onReload()
    } catch (err) {
      onFlash('error', err.response?.data?.message || 'Ошибка загрузки')
    } finally { setUploading(false) }
  }

  const handleDelete = async (docId) => {
    if (!confirm('Удалить документ?')) return
    try {
      await api.delete(`/admin/cars/${carId}/documents/${docId}`)
      onFlash('success', 'Документ удалён')
      onReload()
    } catch (e) {
      onFlash('error', 'Ошибка удаления')
    }
  }

  return (
    <div className="flex-col" style={{ gap: 'var(--gap)' }}>
      {/* Текущие документы по типам */}
      <p className="section-title">Загруженные документы</p>
      {DOC_TYPES.map(({ value, label, icon }) => {
        const doc = docsByType[value]
        return (
          <div key={value} className="card" style={{
            borderColor: doc ? 'rgba(34,197,94,0.3)' : 'var(--border)',
            background:  doc ? 'rgba(34,197,94,0.04)' : 'transparent',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: '1.3rem' }}>{icon}</span>
                <div>
                  <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>{label}</p>
                  {doc && (
                    <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>
                      {doc.comment && <span style={{color:'var(--text)', fontWeight:600}}>{doc.comment} · </span>}
                      {doc.expires_at
                        ? <span style={{color: new Date(doc.expires_at) < new Date() ? 'var(--danger)' : new Date(doc.expires_at) - new Date() < 14*24*60*60*1000 ? 'var(--warning)' : 'inherit'}}>
                            до {new Date(doc.expires_at).toLocaleDateString('ru')}
                            {new Date(doc.expires_at) < new Date() ? ' ⚠️' : ''}
                          </span>
                        : <span>загружено {new Date(doc.uploaded_at).toLocaleDateString('ru')}</span>
                      }
                    </p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {doc
                  ? <>
                      <a href={doc.file_url} target="_blank" rel="noreferrer"
                        className="btn btn--outline"
                        style={{ padding: '4px 10px', fontSize: '0.78rem' }}>
                        👁 Открыть
                      </a>
                      <button onClick={() => handleDelete(doc.id)}
                        style={{ background: 'none', border: 'none', color: 'var(--danger)',
                          cursor: 'pointer', fontSize: '1rem', padding: '4px' }}>
                        🗑
                      </button>
                    </>
                  : <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Не загружен</span>
                }
              </div>
            </div>
          </div>
        )
      })}

      {/* История всех загрузок */}
      {docs.length > 0 && (
        <>
          <p className="section-title">История ({docs.length})</p>
          {docs.map(doc => {
            const dtype = DOC_TYPES.find(d => d.value === doc.doc_type)
            return (
              <div key={doc.id} className="card" style={{ padding: 'var(--gap-sm) var(--gap)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: '0.88rem' }}>{dtype?.icon} {doc.doc_label}</span>
                    <p className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {new Date(doc.uploaded_at).toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <a href={doc.file_url} target="_blank" rel="noreferrer"
                      style={{ color: 'var(--accent)', fontSize: '0.82rem' }}>Открыть</a>
                    <button onClick={() => handleDelete(doc.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--danger)',
                        cursor: 'pointer', fontSize: '0.85rem' }}>Удалить</button>
                  </div>
                </div>
              </div>
            )
          })}
        </>
      )}

      {/* Форма загрузки нового документа */}
      <p className="section-title">Загрузить документ</p>
      <form onSubmit={handleUpload} className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
        <div className="field">
          <label className="field__label">Тип документа</label>
          <select className="input" value={form.doc_type}
            onChange={e => setForm(f => ({ ...f, doc_type: e.target.value, doc_number: '', expires_at: '' }))}>
            {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.icon} {d.label}</option>)}
          </select>
        </div>

        {/* Поле зависит от типа документа */}
        {(() => {
          const dtype = DOC_TYPES.find(d => d.value === form.doc_type)
          if (dtype?.field === 'number') return (
            <div className="field">
              <label className="field__label">{dtype.fieldLabel}</label>
              <input className="input" placeholder={dtype.placeholder}
                value={form.doc_number || ''}
                onChange={e => setForm(f => ({ ...f, doc_number: e.target.value }))} />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                Номер указан на документе
              </span>
            </div>
          )
          if (dtype?.field === 'expires') return (
            <div className="field">
              <label className="field__label">{dtype.fieldLabel} <span style={{color:'var(--danger)'}}>*</span></label>
              <input className="input" type="date" value={form.expires_at}
                onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
            </div>
          )
          return null
        })()}

        <div className="field">
          <label className="field__label">Файл <span style={{ color: 'var(--danger)' }}>*</span></label>
          <div style={{
            border: `2px dashed ${file ? 'var(--success)' : 'var(--border)'}`,
            borderRadius: 'var(--r)', padding: 'var(--gap)', textAlign: 'center',
            cursor: 'pointer', transition: 'border-color 0.15s',
          }} onClick={() => fileRef.current?.click()}>
            {file
              ? <p style={{ color: 'var(--success)', fontSize: '0.88rem' }}>✓ {file.name}</p>
              : <p className="text-muted" style={{ fontSize: '0.85rem' }}>Нажмите для выбора файла (PDF, JPG, PNG)</p>
            }
          </div>
          <input ref={fileRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }}
            onChange={e => setFile(e.target.files[0] || null)} />
        </div>

        <button className="btn btn--primary btn--full" type="submit" disabled={uploading || !file}>
          {uploading ? <span className="spinner" /> : '⬆️ Загрузить'}
        </button>
      </form>
    </div>
  )
}

// ── Таб редактирования ────────────────────────────────────────────────────────
function EditTab({ car, onSaved, onFlash }) {
  const [form, setForm] = useState({
    brand:   car.brand  || '',
    model:   car.model  || '',
    plate:   car.plate  || '',
    vin:     car.vin    || '',
    year:    car.year   || '',
    color:   car.color  || '',
    mileage: car.mileage || 0,
    comment: car.comment || '',
    body_type:     car.body_type || 'other',
    has_tail_lift: !!car.has_tail_lift,
    insurance_expires_at:  car.reminders?.insurance_expires_at  || '',
    inspection_expires_at: car.reminders?.inspection_expires_at || '',
    oil_change_due_km:     car.reminders?.oil_change_due_km     || '',
    oil_change_due_date:   car.reminders?.oil_change_due_date   || '',
    service_due_date:      car.reminders?.service_due_date      || '',
  })
  const [saving, setSaving] = useState(false)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))
  const setBool = k => e => setForm(f => ({ ...f, [k]: e.target.checked }))

  const save = async (e) => {
    e.preventDefault(); setSaving(true)
    try {
      await api.patch(`/admin/cars/${car.id}`, form)
      onSaved()
    } catch (err) {
      // Смена типа кузова при активных повреждениях требует явного
      // подтверждения: точки останутся в истории, но перестанут
      // показываться водителю на приёмке.
      const message = err.response?.data?.message || 'Ошибка сохранения'
      if (err.response?.status === 409 && form.body_type !== car.body_type) {
        if (window.confirm(`${message}\n\nПродолжить?`)) {
          try {
            await api.patch(`/admin/cars/${car.id}`,
              { ...form, confirm_body_type_change: true })
            onSaved()
            return
          } catch (err2) {
            onFlash('error', err2.response?.data?.message || 'Ошибка сохранения')
            return
          } finally { setSaving(false) }
        }
        setSaving(false)
        return
      }
      onFlash('error', message)
    } finally { setSaving(false) }
  }

  return (
    <form onSubmit={save} className="flex-col" style={{ gap: 'var(--gap)' }}>
      <p className="section-title">Основные данные</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-sm)' }}>
        {[
          { k: 'brand', l: 'Марка *' }, { k: 'model', l: 'Модель *' },
          { k: 'plate', l: 'Госномер *' }, { k: 'vin', l: 'VIN' },
          { k: 'year',  l: 'Год', t: 'number' }, { k: 'color', l: 'Цвет' },
        ].map(({ k, l, t }) => (
          <div key={k} className="field" style={{ margin: 0 }}>
            <label className="field__label" style={{ fontSize: '0.78rem' }}>{l}</label>
            <input className="input" type={t || 'text'} value={form[k]} onChange={set(k)} />
          </div>
        ))}
      </div>

      <div className="field">
        <label className="field__label">Пробег (км)</label>
        <input className="input" type="number" value={form.mileage} onChange={set('mileage')} />
      </div>

      <div className="field">
        <label className="field__label">Комментарий</label>
        <textarea className="input" rows={2} value={form.comment} onChange={set('comment')}
          style={{ resize: 'vertical' }} />
      </div>

      <p className="section-title">Кузов</p>
      <div className="card flex-col" style={{ gap: 'var(--gap-sm)' }}>
        <div className="field" style={{ margin: 0 }}>
          <label className="field__label">Тип кузова</label>
          <select className="input" value={form.body_type} onChange={set('body_type')}>
            {BODY_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <p className="body-type__hint">
            Определяет схему повреждений на приёмке. При значении «Не указан»
            схема не показывается — приёмка идёт только по фото.
          </p>
        </div>
        <label className="body-type__toggle">
          <input type="checkbox" checked={form.has_tail_lift}
            onChange={setBool('has_tail_lift')} />
          Гидроборт (добавляет зону на схему)
        </label>
      </div>

      <p className="section-title">Напоминания</p>
      <div className="card flex-col" style={{ gap: 'var(--gap-sm)' }}>
        {[
          { k: 'insurance_expires_at',  l: '🛡 Страховка до' },
          { k: 'inspection_expires_at', l: '🔍 Техосмотр до' },
          { k: 'oil_change_due_date',   l: '🛢 Замена масла до' },
          { k: 'service_due_date',      l: '🔧 Плановое ТО' },
        ].map(({ k, l }) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--gap-sm)' }}>
            <label style={{ fontSize: '0.82rem', minWidth: 160, color: 'var(--text-muted)' }}>{l}</label>
            <input className="input" type="date" value={form[k]} onChange={set(k)}
              style={{ flex: 1, fontSize: '0.88rem' }} />
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--gap-sm)' }}>
          <label style={{ fontSize: '0.82rem', minWidth: 160, color: 'var(--text-muted)' }}>🛢 Замена масла (км)</label>
          <input className="input" type="number" value={form.oil_change_due_km} onChange={set('oil_change_due_km')}
            style={{ flex: 1, fontSize: '0.88rem' }} placeholder="например 100000" />
        </div>
      </div>

      <button className="btn btn--primary btn--full" type="submit" disabled={saving}>
        {saving ? <span className="spinner" /> : '💾 Сохранить изменения'}
      </button>
    </form>
  )
}
